import type { AccountDto } from '../../types';

export interface SOQLResponse<T> {
  done: boolean;
  totalSize: number;
  records: T[];
}

export enum IsoLanguageCodes {
  en = 'en',
  eu = 'eu',
  au = 'au',
}

export enum Currency {
  USD = 'USD',
  GBP = 'GBP',
  AUD = 'AUD',
}

export enum ProductFamilyCode {
  CTS = 'CTS',
  IDA = 'IDA',
  MDR = 'MDR',
  MPT = 'MPT',
  PTS = 'PTS',
  VRA = 'VRA',
  CAI = 'CAI',
}

export interface ProductFamily {
  id: string;
  code: ProductFamilyCode;
  name: string;
  order: number;
  currency: Currency;
  intro: string;
  planHeading: string;
  planSubHeading: string;
  ribbon: string;
  shortDescription: string;
  summary: string;
  isoLanguageCode: IsoLanguageCodes;
  active: boolean;
  upcomingService: boolean;
  logoUrl: string;
  darkLogoUrl: string;
  agreementUrl: string;
  bannerUrl: string;
  brochureUrl: string;
}

export enum ProductCode {
  CTS_BST = 'CTS-BST',
  CTS_WBT = 'CTS-WBT',
  IDA_PRO = 'IDA-PRO',
  MDRE = 'MDRE',
  MDR_ELITE = 'MDR-ELITE',
  MDR_PRO = 'MDR-PRO',
  MDRP = 'MDRP',
  MDR_DFC = 'MDR-DFC',
  MDR_DFP = 'MDR-DFP',
  MDR_DFM = 'MDR-DFM',
  MPT_MPT = 'MPT-MPT',
  PTS_GMA = 'PTS-GMA',
  PTS_GMS = 'PTS-GMS',
  VRA_FRE = 'VRA-FRE',
  VRA_PRO = 'VRA-PRO',
  VRA_ELI = 'VRA-ELI',
  CPS_BSC = 'CPS-BSC',
  CPS_ADV = 'CPS-ADV',
  CPS_CUS = 'CPS-CUS',
  CAI_BSC = 'CAI-BSC',
  CAI_ADV = 'CAI-ADV',
}

export enum PricingModel {
  Standard = 'Standard',
  Block = 'Block',
}

export interface Dimension {
  currency: string;
  name: string;
  type: string;
}

export interface PricingTier {
  lowerBound: number;
  upperBound: number;
  price: number;
  priceType: string;
  currency: string;
}

export interface PriceDetails {
  // Standard pricing fields
  isSubscription?: boolean;
  listPrice?: number;
  termLength?: number;
  yearlyPrice?: number;
  dimensions?: Dimension[];

  // Block pricing fields
  tiers?: PricingTier[];
}

export interface PricingData {
  productId: string;
  currencyCode: string;
  pricingModel: PricingModel;
  priceDetails: PriceDetails;
  active: boolean;
}

export interface Product {
  id: string;
  code: ProductCode;
  name: string;
  callToActionText: string;
  order: number;
  features: string;
  currency: Currency;
  isoLanguageCode: IsoLanguageCodes;
  ribbon: string;
  active: boolean;
  pricingData?: PricingData;
  quantifiable: boolean;
  quantityType: string;
  revenueDiscountThreshold: number | null;
}

export enum ContractStatus {
  'Activated' = 'Activated',
  'In Approval Process' = 'In Approval Process',
  'Draft' = 'Draft',
  'Expired' = 'Expired',
}

export interface Contract {
  id: string;
  status: ContractStatus;
  startDate: string;
  endDate: string;
  product?: Product;
  productFamily?: ProductFamily;
}

export interface Subscription {
  id: string;
  startDate: string;
  endDate: string;
  product?: Product;
  productFamily?: ProductFamily;
  contract: Contract;
}

export interface CRSInterestedProductFamily {
  Name: string;
  CRS_Product_Family__c: string;
  Opportunity__c: string;
}

export interface CRSInterestedProduct {
  Name: string;
  Product__c: string;
  Opportunity__c: string;
}

export interface Opportunity {
  id: string;
  products: Product[];
  productFamilies: ProductFamily[];
}

export interface PreferenceRecordedOpportunity {
  opportunityId: string;
  productFamilyId: string;
  service: string;
  status: ServiceStatus;
  plan: string;
  planId: string;
  startDate: string;
  endDate: string;
}

export interface LeadConvertorRequestBody {
  me: any;
  account: AccountDto;
  policy: {
    policyNumber: string;
    product: string;
  };
  riskFactors: any;
}

export interface LeadConvertorResponseBody {
  accountId: string;
  opportunityId: string;
  contactId: string;
}

export enum MetricType {
  BANNER_CLICK = 'BANNER_CLICK',
  BANNER_IMPRESSION = 'BANNER_IMPRESSION',
  SIDEBAR_NAVIGATION_CLICK = 'SIDEBAR_MENU_ITEM_CLICK',
}

export enum MetricKey {
  DASHBOARD_SERVICE_HALF_BANNER = 'DASHBOARD_SERVICE_HALF_BANNER',
  DASHBOARD_SERVICE_FULL_BANNER = 'DASHBOARD_SERVICE_FULL_BANNER',
  RESILIENCE_CATALOG_PAGE = 'RESILIENCE_CATALOG',
}

export interface Metric {
  metricId: number;
  accountId: string;
  email: string;
  metricType: MetricType;
  metricKey: MetricKey;
  metricValue: number;
  lastRecordedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceStatusInfo {
  product: Product;
  productFamily: ProductFamily;
  status: ServiceStatus;
  startDate: string;
  endDate: string;
}
export enum ServiceStatus {
  NotActivated = 'NotActivated',
  RequestReceived = 'RequestReceived',
  ActivationInProgress = 'ActivationInProgress',
  TestingInProgress = 'TestingInProgress',
  TestingCompleted = 'TestingCompleted',
  Activated = 'Activated',
  Purchased = 'Purchased',
}

export interface ProductButtonConfig {
  text: string;
  onClick: () => void;
  disabled: boolean;
  helperText?: string;
}
